import { DatePicker, Popover, WeekDatePicker } from '@pique/component';
import type React from 'react';

import * as styles from './TodoItemComponent.css';

export type CalendarMode = 'month' | 'week';

export interface QuickDateOption {
  label: string;
  selected?: boolean;
  onClick: () => void;
}

interface ActionItemDatePickerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  trigger: React.ReactNode;
  quickOptions?: QuickDateOption[];
  sectionLabel?: string;
  value: string | undefined;
  onChange: (date: string) => void;
  noDateSelected?: boolean;
  showTimePicker?: boolean;
  time?: string;
  onTimeChange?: (time: string) => void;
  footerContent?: React.ReactNode;
  calendarMode?: CalendarMode;
}

export const ActionItemDatePicker: React.FC<ActionItemDatePickerProps> = ({
  open,
  onOpenChange,
  trigger,
  quickOptions,
  sectionLabel,
  value,
  onChange,
  noDateSelected,
  showTimePicker,
  time,
  onTimeChange,
  footerContent,
  calendarMode = 'month',
}) => {
  const hasQuickOptions = quickOptions && quickOptions.length > 0;

  return (
    <Popover
      open={open}
      onOpenChange={onOpenChange}
      contentOptions={{
        side: 'bottom',
        align: 'start',
        sideOffset: 4,
        style: { padding: 0 },
      }}
      content={
        <div
          className={styles.dueDatePickerContainer}
          onClick={e => e.stopPropagation()}
        >
          {hasQuickOptions && (
            <>
              <div className={styles.dueDateQuickActions}>
                {quickOptions.map((option, index) => (
                  <div
                    key={index}
                    className={styles.dueDateQuickActionRow}
                    data-selected={option.selected || undefined}
                    onClick={e => {
                      e.stopPropagation();
                      option.onClick();
                    }}
                  >
                    {option.label}
                  </div>
                ))}
              </div>
              <div className={styles.dueDateDivider} />
            </>
          )}
          {sectionLabel && (
            <div className={styles.dueDateSectionLabel}>{sectionLabel}</div>
          )}
          <div
            className={styles.dueDateCalendarWrapper}
            data-no-date={noDateSelected || undefined}
            data-mode={calendarMode}
          >
            {calendarMode === 'week' ? (
              <WeekDatePicker value={value} onChange={onChange} />
            ) : (
              <div className={styles.calendar}>
                <DatePicker
                  value={value}
                  onChange={onChange}
                  gapX={4}
                  gapY={4}
                />
              </div>
            )}
          </div>
          {showTimePicker && onTimeChange && (
            <>
              <div className={styles.dueDateDivider} />
              <div className={styles.pickerTimeRow}>
                <span className={styles.pickerTimeLabel}>Time</span>
                <div className={styles.pickerTimeSelectRow}>
                  <select
                    className={styles.pickerTimeSelect}
                    value={time?.split(':')[0] || '09'}
                    onChange={e => {
                      const mins = time?.split(':')[1] || '00';
                      onTimeChange(`${e.target.value}:${mins}`);
                    }}
                    onClick={e => e.stopPropagation()}
                  >
                    {Array.from({ length: 24 }, (_, i) => {
                      const hour = i.toString().padStart(2, '0');
                      const display =
                        i === 0
                          ? '12 AM'
                          : i < 12
                            ? `${i} AM`
                            : i === 12
                              ? '12 PM'
                              : `${i - 12} PM`;
                      return (
                        <option key={hour} value={hour}>
                          {display}
                        </option>
                      );
                    })}
                  </select>
                  <span className={styles.pickerTimeSeparator}>:</span>
                  <select
                    className={styles.pickerTimeSelect}
                    value={time?.split(':')[1] || '00'}
                    onChange={e => {
                      const hrs = time?.split(':')[0] || '09';
                      onTimeChange(`${hrs}:${e.target.value}`);
                    }}
                    onClick={e => e.stopPropagation()}
                  >
                    {['00', '15', '30', '45'].map(min => (
                      <option key={min} value={min}>
                        {min}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </>
          )}
          {footerContent && (
            <>
              <div className={styles.dueDateDivider} />
              <div className={styles.pickerFooter}>{footerContent}</div>
            </>
          )}
        </div>
      }
    >
      {trigger}
    </Popover>
  );
};
